import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  key: 1,
  class: "application-wizard"
}

import { ref, reactive, onBeforeMount, computed } from 'vue';
import { useStore } from 'vuex';
import Loading from '@shell/components/Loading.vue';
import Wizard from '@shell/components/Wizard.vue';

import AppInfo from '../../../../../components/application/AppInfo.vue';
import AppSource from '../../../../../components/application/AppSource.vue';
import AppConfiguration from '../../../../../components/application/AppConfiguration.vue';
import AppProgress from '../../../../../components/application/AppProgress.vue';

import { _CREATE } from '@shell/config/query-params';
import { EpinioAppInfo, EpinioAppBindings, EpinioAppSource, EPINIO_TYPES } from '../../../../../types';
import { createEpinioRoute } from '../../../../../utils/custom-routing';
import { allHash } from '@shell/utils/promise';


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const store = useStore();

const loading = ref(true);
const value = ref<any>(null);
const mode = ref(_CREATE);
const source = ref<EpinioAppSource>();
const bindings = ref<EpinioAppBindings>();
const appChart = reactive({ chartsList: undefined as any, selectedChart: undefined });
const epinioInfo = ref<any>(null);
const originalModel = ref<any>(null);

// i18n
const t = computed(() => store.getters['i18n/t']);

// Steps
const steps = reactive([
  {
    name: 'source',
    label: t.value('epinio.applications.steps.source.label'),
    subtext: t.value('epinio.applications.steps.source.subtext'),
    ready: false
  },
  {
    name: 'basics',
    label: t.value('epinio.applications.steps.basics.label'),
    subtext: t.value('epinio.applications.steps.basics.subtext'),
    ready: false
  },
  {
    name: 'configurations',
    label: t.value('epinio.applications.steps.configurations.label'),
    subtext: t.value('epinio.applications.steps.configurations.subtext'),
    ready: true,
    nextButton: {
      labelKey: 'epinio.applications.steps.configurations.next',
      style: 'btn role-primary bg-warning'
    }
  },
  {
    name: 'progress',
    label: t.value('epinio.applications.steps.progress.label'),
    subtext: t.value('epinio.applications.steps.progress.subtext'),
    ready: false,
    previousButton: { disable: true }
  }
]);

// Fetch data before mount
onBeforeMount(async () => {
  const hash = await allHash({
    ns: store.dispatch('epinio/findAll', { type: EPINIO_TYPES.NAMESPACE }),
    charts: store.dispatch('epinio/findAll', { type: EPINIO_TYPES.APP_CHARTS }),
    info: store.dispatch('epinio/info'),
  });

  epinioInfo.value = hash.info;
  appChart.chartsList = hash.charts;
  originalModel.value = await store.dispatch('epinio/create', { type: EPINIO_TYPES.APP });
  value.value = await store.dispatch('epinio/clone', { resource: originalModel.value });

  loading.value = false;
});

// Methods
function set(obj: Record<string, any>, changes: Record<string, any>) {
  Object.entries(changes).forEach(([key, val]) => {
    obj[key] = val;
  });
}

function updateInfo(changes: EpinioAppInfo) {
  value.value.meta ||= {};
  value.value.configuration ||= {};
  set(value.value.meta, changes.meta);
  set(value.value.configuration, { settings: appChart.settings });
  set(value.value.configuration, changes.configuration);
}

function updateSource(changes: EpinioAppSource) {
  source.value = {};
  const { appChart: chartId, ...cleanChanges } = changes;

  appChart.selectedChart = chartId;
  value.value.configuration ||= {};
  value.value.configuration.settings = undefined;

  if (chartId) {
    set(value.value.configuration, { appchart: chartId });
    const chart = appChart.chartsList?.find((c: any) => c.id === chartId);

    if (chart?.settings) {
      const customSettings = Object.keys(chart.settings).reduce((acc, key) => {
        acc[key] = '';
        return acc;
      }, {} as Record<string, any>);

      set(value.value.configuration, { settings: customSettings });
      set(value.value, { chart });
    }
  }

  set(source.value, cleanChanges);
}

function updateManifestConfigurations(configs: string[]) {
  set(value.value.configuration, { configurations: configs });
}

function updateConfigurations(changes: EpinioAppBindings) {
  bindings.value = {};
  set(bindings.value, changes);
  set(value.value.configuration, [...changes.configurations]);
}

function cancel() {
  store.$router.replace(value.value.listLocation);
}

function finish() {
  const route = createEpinioRoute('c-cluster-resource-id', {
    cluster: store.getters['clusterId'],
    resource: value.value.type,
    id: `${value.value.meta.namespace}/${value.value.meta.name}`
  });

  store.$router.replace(route);
}

return (_ctx: any,_cache: any) => {
  return (loading.value)
    ? (_openBlock(), _createBlock(Loading, { key: 0 }))
    : (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createVNode(Wizard, {
          steps: steps,
          "banner-title": t.value('epinio.applications.create.title'),
          "banner-title-subtext": t.value('epinio.applications.create.titleSubText'),
          "header-mode": "create",
          "finish-mode": "done",
          "edit-first-step": true,
          class: "wizard",
          onCancel: cancel,
          onFinish: finish
        }, {
          source: _withCtx(() => [
            _createVNode(AppSource, {
              application: value.value,
              source: source.value,
              mode: mode.value,
              info: epinioInfo.value,
              onChange: updateSource,
              onChangeAppInfo: updateInfo,
              onChangeAppConfig: updateManifestConfigurations,
              onValid: _cache[0] || (_cache[0] = (val) => steps[0].ready = val)
            }, null, 8, ["application", "source", "mode", "info"])
          ]),
          basics: _withCtx(() => [
            _createVNode(AppInfo, {
              application: value.value,
              mode: mode.value,
              onChange: updateInfo,
              onValid: _cache[1] || (_cache[1] = (val) => steps[1].ready = val)
            }, null, 8, ["application", "mode"])
          ]),
          configurations: _withCtx(() => [
            _createVNode(AppConfiguration, {
              application: value.value,
              mode: mode.value,
              onChange: updateConfigurations
            }, null, 8, ["application", "mode"])
          ]),
          progress: _withCtx(({ step }) => [
            _createVNode(AppProgress, {
              application: value.value,
              source: source.value,
              bindings: bindings.value,
              mode: mode.value,
              step: step
            }, null, 8, ["application", "source", "bindings", "mode", "step"])
          ]),
          _: 1,
          __: [2,3,4]
        }, 8, ["steps", "banner-title", "banner-title-subtext"])
      ]))
}
}

})